import { Controller } from "@hotwired/stimulus";

// Shared global counter for open dialogs (modals and slideovers)
// This ensures proper scrollbar compensation when mixing modals and slideovers
if (!window.__openDialogCount) {
  window.__openDialogCount = 0;
}

// Reset dialog count on page navigation to prevent desync issues
// This handles cases where users navigate back/forward while dialogs are open
if (!window.__dialogCountResetBound) {
  window.__dialogCountResetBound = true;

  const resetDialogCount = () => {
    // Only reset if no dialogs are actually open
    const openDialogs = document.querySelectorAll("dialog[open]");
    if (openDialogs.length === 0) {
      window.__openDialogCount = 0;
      document.documentElement.style.removeProperty("--scrollbar-compensation");
      document.body.classList.remove("modal-open", "slideover-open");
    } else {
      // Sync count with actual open dialogs
      window.__openDialogCount = openDialogs.length;
    }
  };

  document.addEventListener("turbo:before-cache", resetDialogCount);
  document.addEventListener("turbo:load", resetDialogCount);
}

export default class extends Controller {
  static targets = ["dialog", "template"];
  static values = {
    open: { type: Boolean, default: false }, // Whether the slideover is open
    lazyLoad: { type: Boolean, default: false }, // Whether to lazy load the slideover content
    turboFrameSrc: { type: String, default: "" }, // URL for the turbo frame
    autoFocus: { type: Boolean, default: false }, // True: focus first focusable element on open. False: don't focus on anything by default. (if an element has autofocus="true", it will focus on it whether this option is true or false)
  };

  connect() {
    if (!this.hasDialogTarget) return;

    // Initialize state
    this.contentLoaded = false;
    this.isOpen = false; // Track if this specific slideover is open
    this.isOpening = false; // Prevent duplicate opens while lazy content is loading

    // Initialize focus trapping
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    // Check if dialog is already open (from previous page state) and close it
    if (this.dialogTarget.open) {
      // Mark as open temporarily to allow cleanup
      this.isOpen = true;
      this.dialogTarget.close();
      this.cleanupScrollbarCompensation();
    }

    // Now handle the intended open state
    if (this.openValue) this.open();

    // Set up event listeners
    this.boundBeforeCache = this.beforeCache.bind(this);
    this.boundBeforeVisit = this.beforeVisit.bind(this);
    document.addEventListener("turbo:before-cache", this.boundBeforeCache);
    document.addEventListener("turbo:before-visit", this.boundBeforeVisit);

    // Add event listener for when dialog is closed by any means (including Escape key)
    this.boundHandleDialogClose = this.handleDialogClose.bind(this);
    this.dialogTarget.addEventListener("close", this.boundHandleDialogClose);

    // Additional keydown listener for focus trapping
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.dialogTarget.addEventListener("keydown", this.boundHandleKeydown);
  }

  disconnect() {
    // If this slideover was open when disconnecting, clean up scrollbar compensation
    if (this.isOpen) {
      this.cleanupScrollbarCompensation();
    }

    document.removeEventListener("turbo:before-cache", this.boundBeforeCache);
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit);
    this.dialogTarget.removeEventListener("close", this.boundHandleDialogClose);
    this.dialogTarget.removeEventListener("keydown", this.boundHandleKeydown);
  }

  async open(event) {
    // If already open, don't do anything
    if (this.isOpen || this.isOpening) return;

    this.isOpening = true;
    const triggerElement =
      event?.currentTarget instanceof HTMLElement &&
      event.currentTarget.matches('button, [role="button"], input[type="button"], input[type="submit"], a[href]')
        ? event.currentTarget
        : null;
    const shouldShowLoadingState = this.lazyLoadValue && !this.contentLoaded;
    let lockAcquired = false;

    try {
      // Acquire scrollbar compensation immediately so page scroll is locked
      // before any async lazy-loading delay.
      window.__openDialogCount++;
      this.isOpen = true;
      lockAcquired = true;

      if (window.__openDialogCount === 1) {
        const scrollbarWidth = this.getScrollbarWidth();
        if (scrollbarWidth > 0) {
          document.documentElement.style.setProperty("--scrollbar-compensation", `${scrollbarWidth}px`);
        } else {
          document.documentElement.style.removeProperty("--scrollbar-compensation");
        }
      }
      document.body.classList.add("slideover-open");

      // If lazy loading is enabled and content hasn't been loaded yet, load it now
      if (shouldShowLoadingState) {
        this.setTriggerLoadingState(triggerElement, true);
        try {
          await this.#loadTemplateContent();
          this.contentLoaded = true;
        } finally {
          this.setTriggerLoadingState(triggerElement, false);
        }
      }

      this.dialogTarget.showModal();

      // Set up focus trapping
      this.setupFocusTrapping();
    } catch (error) {
      // Roll back provisional lock if opening fails (e.g. lazy-load error).
      if (lockAcquired && this.isOpen) {
        this.cleanupScrollbarCompensation();
      }
      throw error;
    } finally {
      this.isOpening = false;
    }
  }

  close() {
    // If not open, don't do anything
    if (!this.isOpen) return;

    this.dialogTarget.setAttribute("closing", "");

    Promise.allSettled(this.dialogTarget.getAnimations().map((animation) => animation.finished)).then(() => {
      this.dialogTarget.removeAttribute("closing");
      this.dialogTarget.close();

      // The handleDialogClose method will handle the cleanup when the dialog actually closes
      // This prevents double cleanup and ensures proper timing
    });
  }

  backdropClose(event) {
    // Only close if clicking on the actual backdrop area (outside the dialog's content box)
    if (event.target.nodeName === "DIALOG") {
      // Get the dialog's bounding rect to check if click is outside content area
      const rect = this.dialogTarget.getBoundingClientRect();
      // Click is on backdrop if it's outside the dialog's content box
      // This prevents scrollbar clicks from being treated as backdrop clicks
      const isBackdropClick =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;

      if (isBackdropClick) {
        // Stop propagation to prevent closing parent dialogs
        event.stopPropagation();

        this.close();
      }
    }
  }

  show(event) {
    this.open(event);
  }

  hide(event) {
    if (event) event.preventDefault();
    this.close();
  }

  beforeCache() {
    // Close immediately without animation during navigation
    if (this.isOpen) {
      this.dialogTarget.removeAttribute("closing");
      this.dialogTarget.close();
      this.cleanupScrollbarCompensation();
    }
  }

  beforeVisit() {
    // Close immediately without animation during navigation
    if (this.isOpen) {
      this.dialogTarget.removeAttribute("closing");
      this.dialogTarget.close();
      this.cleanupScrollbarCompensation();
    }
  }

  // Calculate actual scrollbar width
  getScrollbarWidth() {
    // Create a temporary div to measure scrollbar width
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    outer.style.msOverflowStyle = "scrollbar"; // Force scrollbars on IE/Edge
    document.body.appendChild(outer);

    const inner = document.createElement("div");
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
  }

  setTriggerLoadingState(triggerElement, isLoading) {
    if (!(triggerElement instanceof HTMLElement)) return;

    const supportsDisabled = "disabled" in triggerElement;

    if (isLoading) {
      triggerElement.classList.add("!cursor-wait");
      triggerElement.setAttribute("aria-busy", "true");

      if (supportsDisabled) {
        triggerElement.dataset.overlayOriginalDisabled = triggerElement.disabled ? "true" : "false";
        triggerElement.disabled = true;
      } else {
        triggerElement.dataset.overlayOriginalAriaDisabled = triggerElement.getAttribute("aria-disabled") || "";
        triggerElement.dataset.overlayHadPointerEventsNone = triggerElement.classList.contains("pointer-events-none")
          ? "true"
          : "false";
        triggerElement.setAttribute("aria-disabled", "true");
        triggerElement.classList.add("pointer-events-none");
      }

      return;
    }

    triggerElement.classList.remove("!cursor-wait");
    triggerElement.removeAttribute("aria-busy");

    if (supportsDisabled) {
      triggerElement.disabled = triggerElement.dataset.overlayOriginalDisabled === "true";
      delete triggerElement.dataset.overlayOriginalDisabled;
    } else {
      const originalAriaDisabled = triggerElement.dataset.overlayOriginalAriaDisabled;
      if (originalAriaDisabled) {
        triggerElement.setAttribute("aria-disabled", originalAriaDisabled);
      } else {
        triggerElement.removeAttribute("aria-disabled");
      }

      if (triggerElement.dataset.overlayHadPointerEventsNone !== "true") {
        triggerElement.classList.remove("pointer-events-none");
      }

      delete triggerElement.dataset.overlayOriginalAriaDisabled;
      delete triggerElement.dataset.overlayHadPointerEventsNone;
    }
  }

  async #loadTemplateContent() {
    // Find the container in the dialog to append content to
    const container = this.dialogTarget.querySelector("[data-slideover-content]") || this.dialogTarget;

    // Check if we should use Turbo Frame lazy loading
    if (this.turboFrameSrcValue) {
      // Look for a turbo-frame in the container
      let turboFrame = container.querySelector("turbo-frame");

      if (!turboFrame) {
        // Create a turbo-frame if it doesn't exist
        turboFrame = document.createElement("turbo-frame");
        turboFrame.id = "slideover-lazy-content";

        // Clear any loading indicators or placeholder content
        container.innerHTML = "";
        container.appendChild(turboFrame);
      }

      // Set the src to trigger the lazy load
      turboFrame.src = this.turboFrameSrcValue;

      // Wait for the turbo-frame to load
      return new Promise((resolve) => {
        const handleLoad = () => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        };

        turboFrame.addEventListener("turbo:frame-load", handleLoad);

        // Fallback timeout in case the frame doesn't load
        setTimeout(() => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        }, 5000);
      });
    } else if (this.hasTemplateTarget) {
      // Use template-based lazy loading
      const templateContent = this.templateTarget.content.cloneNode(true);

      // Clear any loading indicators or placeholder content
      container.innerHTML = "";

      // Append the template content
      container.appendChild(templateContent);
    }
  }

  // Handle cleanup when dialog is closed by any means
  handleDialogClose() {
    // If the slideover was open, decrement the counter
    if (this.isOpen) {
      this.cleanupScrollbarCompensation();
    }

    // Ensure the closing attribute is removed
    this.dialogTarget.removeAttribute("closing");
  }

  // Centralized method to handle scrollbar compensation cleanup
  cleanupScrollbarCompensation() {
    if (!this.isOpen) return;

    window.__openDialogCount = Math.max(0, window.__openDialogCount - 1);
    this.isOpen = false;

    // Only remove scrollbar compensation if no dialogs are open
    if (window.__openDialogCount === 0) {
      document.documentElement.style.removeProperty("--scrollbar-compensation");
      document.body.classList.remove("modal-open", "slideover-open");
    } else {
      this.syncBodyOverlayClasses();
    }
  }

  // Keep body classes aligned with actual open overlay types when dialogs are nested
  syncBodyOverlayClasses() {
    const hasOpenModalDialog = document.querySelector('dialog[open][data-modal-target="dialog"]');
    const hasOpenModalDiv = document.querySelector('[data-modal-target="dialog"].modal-open');
    const hasOpenSlideover = document.querySelector('dialog[open][data-slideover-target="dialog"]');

    document.body.classList.toggle("modal-open", Boolean(hasOpenModalDialog || hasOpenModalDiv));
    document.body.classList.toggle("slideover-open", Boolean(hasOpenSlideover));
  }

  // Handle keydown events for focus trapping
  handleKeydown(event) {
    // Handle Tab key for focus trapping
    if (event.key === "Tab") {
      this.handleTabKey(event);
    }
  }

  // Set up focus trapping for the slideover
  setupFocusTrapping() {
    // Get all focusable elements within the slideover
    this.updateFocusableElements();

    // Check for elements with native autofocus attribute first
    const autofocusElement = this.dialogTarget.querySelector("[autofocus]");
    if (autofocusElement) {
      // Always respect native autofocus, regardless of device type
      autofocusElement.focus();
    } else if (this.autoFocusValue && this.firstFocusableElement) {
      // Fall back to auto-focusing first element if no native autofocus
      this.firstFocusableElement.focus();
    }
  }

  // Update the list of focusable elements
  updateFocusableElements() {
    const focusableSelector = [
      "a[href]",
      "area[href]",
      'input:not([disabled]):not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      "details",
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]:not([contenteditable="false"])',
    ].join(",");

    this.focusableElements = Array.from(this.dialogTarget.querySelectorAll(focusableSelector)).filter((element) => {
      // Filter out elements that are not visible or have display: none
      return (
        element.offsetWidth > 0 &&
        element.offsetHeight > 0 &&
        getComputedStyle(element).display !== "none" &&
        getComputedStyle(element).visibility !== "hidden"
      );
    });

    this.firstFocusableElement = this.focusableElements[0] || null;
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1] || null;
  }

  // Handle Tab key for focus trapping
  handleTabKey(event) {
    // Update focusable elements in case the DOM has changed
    this.updateFocusableElements();

    if (this.focusableElements.length === 0) {
      // If no focusable elements, prevent tab and keep focus on dialog
      event.preventDefault();
      return;
    }

    if (this.focusableElements.length === 1) {
      // If only one focusable element, prevent tab and keep focus on it
      event.preventDefault();
      this.firstFocusableElement.focus();
      return;
    }

    // Handle normal tab navigation with wrapping
    if (event.shiftKey) {
      // Shift+Tab: moving backwards
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // Tab: moving forwards
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }
}
